import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Lock } from '@mui/icons-material';
import { authService } from '../services/authService';

interface UpdateProfileDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

const UpdateProfileDialog: React.FC<UpdateProfileDialogProps> = ({ open, onClose, onUpdated }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [isCancellingEmailChange, setIsCancellingEmailChange] = useState(false);

  // Reload from the signed-in user each time the dialog opens so edits never start from stale values
  useEffect(() => {
    if (!open) return;

    const user = authService.getUser();
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
    setEmail(user?.email ?? '');
    setOriginalEmail(user?.email ?? '');
    setPendingEmail(user?.pendingEmail ?? null);
    setEmailPassword('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setProfileError(null);
    setProfileSuccess(null);
    setPasswordError(null);
    setPasswordSuccess(null);
  }, [open]);

  const emailChanged = email.trim().toLowerCase() !== originalEmail.trim().toLowerCase();

  const handleCancelEmailChange = async () => {
    setProfileError(null);
    setProfileSuccess(null);
    setIsCancellingEmailChange(true);
    try {
      const result = await authService.cancelEmailChange();
      if (!result.success) {
        setProfileError(result.error || 'Could not cancel the pending email change.');
        return;
      }
      setPendingEmail(null);
      setProfileSuccess('The pending email change has been cancelled.');
      onUpdated?.();
    } finally {
      setIsCancellingEmailChange(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileError(null);
    setProfileSuccess(null);

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setProfileError('First name, last name, and email are all required.');
      return;
    }

    if (emailChanged && !emailPassword) {
      setProfileError('Enter your current password to change your email address.');
      return;
    }

    setIsSavingProfile(true);
    try {
      const result = await authService.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        currentPassword: emailChanged ? emailPassword : undefined,
      });

      if (!result.success) {
        setProfileError(result.error || 'Could not update your profile. Please try again.');
        return;
      }

      // The API only stages the new address, so keep comparing against the still-live one
      setOriginalEmail(result.user?.email ?? originalEmail);
      setEmailPassword('');
      setEmail(result.user?.email ?? originalEmail);
      setPendingEmail(result.emailConfirmationPending ? result.pendingEmail ?? null : null);
      setProfileSuccess(
        result.emailConfirmationPending
          ? `Saved. We sent a confirmation link to ${result.pendingEmail} — click it to finish switching your email address. Until then, keep signing in with your current address.`
          : 'Your profile has been updated.'
      );
      onUpdated?.();
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Fill in all three password fields.');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Your new password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('The new passwords do not match.');
      return;
    }

    setIsSavingPassword(true);
    try {
      const result = await authService.changePassword({ currentPassword, newPassword });

      if (!result.success) {
        setPasswordError(result.error || 'Could not change your password. Please try again.');
        return;
      }

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess('Your password has been changed.');
      onUpdated?.();
    } finally {
      setIsSavingPassword(false);
    }
  };

  const passwordVisibilityToggle = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        onClick={() => setShowPassword(!showPassword)}
        edge="end"
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
        <Person /> Update Profile
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Change the name and email address on your account.
          </Typography>

          <Box display="flex" gap={2}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              fullWidth
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              fullWidth
            />
          </Box>

          {pendingEmail && (
            <Alert
              severity="warning"
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleCancelEmailChange}
                  disabled={isCancellingEmailChange}
                >
                  {isCancellingEmailChange ? 'Cancelling…' : 'Cancel'}
                </Button>
              }
            >
              Waiting for confirmation at <strong>{pendingEmail}</strong>. Your current address stays
              active until you click the link in that email.
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            fullWidth
          />

          {emailChanged && (
            <>
              <Alert severity="info">
                Your email is also your sign-in name, so confirm your current password to change it.
              </Alert>
              <TextField
                label="Current Password"
                type={showPassword ? 'text' : 'password'}
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                autoComplete="current-password"
                fullWidth
                InputProps={{ endAdornment: passwordVisibilityToggle }}
              />
            </>
          )}

          {profileError && <Alert severity="error">{profileError}</Alert>}
          {profileSuccess && <Alert severity="success">{profileSuccess}</Alert>}

          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveProfile}
            disabled={isSavingProfile}
            fullWidth
          >
            {isSavingProfile ? 'Saving…' : 'Save Profile'}
          </Button>

          <Divider sx={{ my: 1 }} />

          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
            <Lock fontSize="small" /> Change Password
          </Typography>

          <TextField
            label="Current Password"
            type={showPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            fullWidth
            InputProps={{ endAdornment: passwordVisibilityToggle }}
          />
          <TextField
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            helperText="At least 8 characters"
            fullWidth
            InputProps={{ endAdornment: passwordVisibilityToggle }}
          />
          <TextField
            label="Confirm New Password"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            fullWidth
            InputProps={{ endAdornment: passwordVisibilityToggle }}
          />

          {passwordError && <Alert severity="error">{passwordError}</Alert>}
          {passwordSuccess && <Alert severity="success">{passwordSuccess}</Alert>}

          <Button
            variant="outlined"
            color="primary"
            onClick={handleChangePassword}
            disabled={isSavingPassword}
            fullWidth
          >
            {isSavingPassword ? 'Changing…' : 'Change Password'}
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateProfileDialog;
